<script setup lang="ts">
  import BaseAccordion from '@/shared/components/rebranding/atoms/BaseAccordion.vue'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import type { CoversListType } from '@/shared/types/other.types'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'

  interface Props {
    includes?: CoversListType[]
    hasCovers?: boolean
    quoteId?: string
  }
  interface Emit {
    (e: 'show:covers'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    includes: () => [],
    hasCovers: true,
    quoteId: '',
  })
  const emit = defineEmits<Emit>()
  const gaService = useGoogleAnalytics()
  const sendInclusiveCoversClick = (learnMore = false) => {
    gaService.sendQuoteInclusiveCoversClick(learnMore ? 'learn_more' : 'expand_on', props.quoteId)
    if (learnMore) {
      emit('show:covers')
    }
  }
</script>

<template>
  <BaseAccordion
    class="quote-inclusive"
    :class="{ 'quote-inclusive--nolist': !includes.length }"
    arrow-position="right"
    data-test="quote-inclusive-accordion-expand"
    @accordion-click="sendInclusiveCoversClick(false)"
  >
    <template #name>
      {{ $t('qic_quote_inclusive_covers') }}
    </template>

    <template #content>
      <ul v-if="includes.length" class="quote-inclusive__list">
        <li v-for="(include, index) of includes" :key="index">
          <p class="text-medium">{{ $t(include.name) }}</p>
          <span>{{ $t(include.description) }}</span>
        </li>
      </ul>

      <div v-if="hasCovers" class="quote-inclusive__footer">
        <ButtonQC
          variant="secondary"
          size="large"
          wide
          data-test="quote-inclusive-learn-more"
          @click="sendInclusiveCoversClick(true)"
          >{{ $t('qic_quote_learn_more') }}</ButtonQC
        >
      </div>
    </template>
  </BaseAccordion>
</template>

<style lang="scss">
  .quote-inclusive {
    .accordion-header__wrapper {
      display: flex;
      align-items: center;
      gap: var(--spacing-3);
    }
  }

  .quote-inclusive__list {
    border-top: 1px solid var(--colorBrdrSrfTertiary);
    margin-top: var(--spacing-5);

    li {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-2);
      padding-block: var(--spacing-6);

      &:not(:last-child) {
        border-bottom: 1px solid var(--colorBrdrSrfTertiary);
      }
    }

    span {
      color: var(--colorTxtSrfSecondary);
    }
  }
  .quote-inclusive__footer {
    padding-top: var(--spacing-5);
  }

  .ltr {
    direction: ltr !important;
  }
</style>
